import { createBottomTabNavigator } from "@react-navigation/bottom-tabs";
import {
  createStaticNavigation,
  StaticParamList,
} from "@react-navigation/native";
import {
  createNativeStackNavigator,
  NativeStackNavigationOptions,
} from "@react-navigation/native-stack";
import { isAuthenticated, isGuest } from "../services";
import { HomeScreen } from "./home";
import { LoginScreen } from "./login";
import { ProfileScreen } from "./profile";
import { BodyScreen } from "./body";
import { LanguagesScreen } from "./languages";
import { RegisterScreen } from "./register";
import { LoginOtpScreen } from "./login-otp";
import { TabBarIcon, UserOnlineIndicator } from "../components";
import { colors } from "../theme";
import { AuthScreen } from "./auth";
import { ServicesScreen } from "./services";
import { RatesScreen } from "./rates";
import { PhotosScreen } from "./photos";
import { TimelineCreateScreen } from "./timeline-create";
import { TimelineScreen } from "./timeline";
import { IdentityScreen } from "./identity";
import { VerifyIdentityScreen } from "./verify-identity";
import { SocialsScreen } from "./socials";
import { IncallScreen } from "./incall";
import { CityAreasScreen } from "./city-areas";
import { Row } from "../lib";
import { BookingShowScreen } from "./booking-show";
import { BookingCancelScreen } from "./booking-cancel";
import { PremiumScreen } from "./premium";
import { ReviewsScreen } from "./reviews";
import { BookingReportScreen } from "./booking-report";
import { BookingReviewScreen } from "./booking-review";
import { ChatsScreen } from "./chats";
import { ChatScreen } from "./chat";
import { ContactScreen } from "./contact";

const defaultStackOptions: NativeStackNavigationOptions = {
  headerBackButtonDisplayMode: "minimal",
  headerBackImageSource: require("../images/back-icon.png"),
};

const MainNavigator = createBottomTabNavigator({
  screenOptions: ({ route }) => ({
    tabBarIcon: ({ focused }) => (
      <TabBarIcon active={focused} routeName={route.name} />
    ),
    tabBarActiveTintColor: colors.onSurface,
    tabBarInactiveTintColor: colors.onSurfaceDim,
    tabBarActiveBackgroundColor: colors.surface,
    tabBarInactiveBackgroundColor: colors.surface,
    headerRight: () => (
      <Row.R pr="4">
        <UserOnlineIndicator />
      </Row.R>
    ),
    lazy: false,
  }),

  screens: {
    Home: {
      screen: HomeScreen,
      options: {
        title: "Bookings",
      },
    },

    Timeline: {
      screen: TimelineScreen,
    },
    Chats: ChatsScreen,
    Profile: ProfileScreen,
  },
});

const RootNavigator = createNativeStackNavigator({
  screenOptions: {
    ...defaultStackOptions,
  },
  screens: {
    // Common screens
  },
  groups: {
    Authenticated: {
      if: isAuthenticated,
      screens: {
        Main: {
          screen: MainNavigator,
          options: {
            headerShown: false,
          },
        },
        Identity: {
          screen: IdentityScreen,
          options: {
            title: "Edit profile",
          },
        },
        Body: {
          screen: BodyScreen,
          options: {
            title: "Edit measurements",
          },
        },
        Languages: {
          screen: LanguagesScreen,
          options: {
            title: "Edit languages",
          },
        },
        Services: {
          screen: ServicesScreen,
          options: {
            title: "Edit services",
          },
        },
        Rates: {
          screen: RatesScreen,
          options: {
            title: "Edit rates",
          },
        },
        Photos: {
          screen: PhotosScreen,
          options: {
            title: "Edit photos",
          },
        },
        Timeline: TimelineScreen,
        TimelineCreate: {
          screen: TimelineCreateScreen,
          options: {
            title: "Post status",
          },
        },
        VerifyIdentity: {
          screen: VerifyIdentityScreen,
          options: {
            title: "Verify identity",
          },
        },
        Socials: {
          screen: SocialsScreen,
          options: {
            title: "Contact details",
          },
        },
        Incall: {
          screen: IncallScreen,
          options: {
            title: "Edit incall location",
          },
        },
        CityAreas: {
          screen: CityAreasScreen,
          options: {
            title: "Edit city areas",
          },
        },
        BookingShow: {
          screen: BookingShowScreen,
          options: {
            title: "Booking details",
          },
        },
        BookingCancel: {
          screen: BookingCancelScreen,
          options: {
            title: "Cancel booking",
          },
        },
        BookingReport: {
          screen: BookingReportScreen,
          options: {
            title: "Report client",
          },
        },
        BookingReview: {
          screen: BookingReviewScreen,
          options: {
            title: "Rate client",
          },
        },
        Premium: {
          screen: PremiumScreen,
          options: {
            title: "Go Premium",
          },
        },
        Reviews: {
          screen: ReviewsScreen,
          options: {
            title: "Reviews",
          },
        },
        Chat: {
          screen: ChatScreen,
          options: ({ route }) => ({
            // @ts-ignore
            title: route.params?.userName ?? "Chat",
          }),
        },
        Contact: {
          screen: ContactScreen,
          options: {
            title: "Contact",
          },
        },
      },
    },
    Guest: {
      if: isGuest,
      screens: {
        Auth: {
          screen: AuthScreen,
          options: {
            title: "Welcome to Smooci",
            headerShown: false,
          },
        },
        Login: {
          screen: LoginScreen,
          options: {
            title: "Request OTP",
          },
        },
        LoginOtp: {
          screen: LoginOtpScreen,
          options: {
            title: "Sign in",
          },
        },
        Register: {
          screen: RegisterScreen,
          options: {
            title: "Create account",
          },
        },
      },
    },
  },
});

type RootNavigatorParamList = StaticParamList<typeof RootNavigator>;

declare global {
  namespace ReactNavigation {
    // eslint-disable-next-line @typescript-eslint/no-empty-object-type
    interface RootParamList extends RootNavigatorParamList {}
  }
}

export const Navigation = createStaticNavigation(RootNavigator);
